/**
 * Typefaces.
 *
 * Every one of these ships inside the app (see the imports in index.css), so
 * choosing one works offline and looks the same on every machine. They are
 * only downloaded by the browser engine when actually used.
 */

export type FontDef = {
  id: string;
  name: string;
  note: string;
  stack: string;
  group: 'Clean' | 'Friendly' | 'Display' | 'Readable' | 'Mono' | 'Name';
  /**
   * Ships one weight only, so it must never be asked for bold.
   *
   * A blackletter or a pixel face has no bold cut; ask for one and the
   * engine smears the glyphs sideways to fake it, which on Silkscreen looks
   * like a rendering bug and on UnifrakturMaguntia like a blot. Anything
   * drawing one of these pins the weight to 400 - see UserName.
   */
  single?: boolean;
  /** Offered for a name but not for the whole app: unreadable at 13px in a
      list of two hundred, which is what the app font has to survive. */
  nameOnly?: boolean;
};

const SANS = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

export const FONTS: FontDef[] = [
  { id: 'inter', name: 'Inter', note: 'The Pugo default', group: 'Clean', stack: "'Inter Variable', 'Inter', " + SANS },
  { id: 'system', name: 'System', note: 'Your Windows font', group: 'Clean', stack: "'Segoe UI Variable Text', 'Segoe UI', " + SANS },
  { id: 'jakarta', name: 'Plus Jakarta Sans', note: 'Crisp and modern', group: 'Clean', stack: "'Plus Jakarta Sans Variable', " + SANS },
  { id: 'manrope', name: 'Manrope', note: 'Geometric, calm', group: 'Clean', stack: "'Manrope Variable', " + SANS },
  { id: 'dmsans', name: 'DM Sans', note: 'Low contrast, tidy', group: 'Clean', stack: "'DM Sans Variable', " + SANS },
  { id: 'figtree', name: 'Figtree', note: 'Friendly geometric', group: 'Friendly', stack: "'Figtree Variable', " + SANS },
  { id: 'outfit', name: 'Outfit', note: 'Round and bold', group: 'Friendly', stack: "'Outfit Variable', " + SANS },
  { id: 'nunito', name: 'Nunito', note: 'Soft, rounded ends', group: 'Friendly', stack: "'Nunito Variable', " + SANS },
  { id: 'rubik', name: 'Rubik', note: 'Slightly rounded', group: 'Friendly', stack: "'Rubik Variable', " + SANS },
  { id: 'poppins', name: 'Poppins', note: 'Classic geometric', group: 'Display', stack: "'Poppins', " + SANS },
  { id: 'montserrat', name: 'Montserrat', note: 'Wide and confident', group: 'Display', stack: "'Montserrat Variable', " + SANS },
  { id: 'sora', name: 'Sora', note: 'Techy and open', group: 'Display', stack: "'Sora Variable', " + SANS },
  { id: 'grotesk', name: 'Space Grotesk', note: 'Gaming, distinctive', group: 'Display', stack: "'Space Grotesk Variable', " + SANS },
  { id: 'lexend', name: 'Lexend', note: 'Designed for easier reading', group: 'Readable', stack: "'Lexend Variable', " + SANS },
  { id: 'atkinson', name: 'Atkinson Hyperlegible', note: 'Made for low vision', group: 'Readable', stack: "'Atkinson Hyperlegible', " + SANS },
  { id: 'jetbrains', name: 'JetBrains Mono', note: 'Even widths', group: 'Mono', stack: "'JetBrains Mono Variable', 'Cascadia Mono', ui-monospace, monospace" },

  /*
   * The name faces.
   *
   * Display and handwriting cuts, offered for a name and nothing else. Each
   * is good at 18px over a banner and unusable as the font the whole app is
   * set in - which is the job the ones above have to survive, at 13px, in a
   * list of two hundred rows.
   *
   * All but one ship a single weight, so 'single' is set and nothing asks
   * them for bold. Edu QLD Hand has real 400 to 700 cuts, so it does not
   * need it.
   */
  { id: 'unifraktur', name: 'UnifrakturMaguntia', note: 'Blackletter, ornate', group: 'Name', single: true, nameOnly: true, stack: "'UnifrakturMaguntia', serif" },
  { id: 'germania', name: 'Germania One', note: 'Blackletter, solid', group: 'Name', single: true, nameOnly: true, stack: "'Germania One', serif" },
  { id: 'oi', name: 'Oi', note: 'Loud and fat', group: 'Name', single: true, nameOnly: true, stack: "'Oi', cursive" },
  { id: 'bungeeinline', name: 'Bungee Inline', note: 'Outlined, signage', group: 'Name', single: true, nameOnly: true, stack: "'Bungee Inline', cursive" },
  { id: 'knewave', name: 'Knewave', note: 'Thick and blobby', group: 'Name', single: true, nameOnly: true, stack: "'Knewave', cursive" },
  { id: 'silkscreen', name: 'Silkscreen', note: 'Pixel', group: 'Name', single: true, nameOnly: true, stack: "'Silkscreen', monospace" },
  { id: 'jimnightshade', name: 'Jim Nightshade', note: 'Vintage, spidery', group: 'Name', single: true, nameOnly: true, stack: "'Jim Nightshade', cursive" },
  { id: 'hennypenny', name: 'Henny Penny', note: 'Playful, wobbly', group: 'Name', single: true, nameOnly: true, stack: "'Henny Penny', cursive" },
  { id: 'cherrycream', name: 'Cherry Cream Soda', note: 'Happy, retro', group: 'Name', single: true, nameOnly: true, stack: "'Cherry Cream Soda', cursive" },
  { id: 'storyscript', name: 'Story Script', note: 'Active, flowing', group: 'Name', single: true, nameOnly: true, stack: "'Story Script', cursive" },
  { id: 'shadowsintolight', name: 'Shadows Into Light', note: 'Informal handwriting', group: 'Name', single: true, nameOnly: true, stack: "'Shadows Into Light', cursive" },
  { id: 'eduqld', name: 'Edu QLD Hand', note: 'Childlike, schoolbook', group: 'Name', nameOnly: true, stack: "'Edu QLD Hand', cursive" }
];

/** Old ids that people may have saved. */
const ALIASES: Record<string, string> = { rounded: 'nunito', mono: 'jetbrains' };

export function fontById(id: string | undefined | null): FontDef {
  const want = ALIASES[id ?? ''] ?? id ?? 'inter';
  return FONTS.find((f) => f.id === want) ?? FONTS[0]!;
}

/** How big the whole app is drawn. The zoom is applied to the page itself. */
export const SCALES: { id: string; label: string; zoom: number }[] = [
  { id: 'xs', label: '85%', zoom: 0.85 },
  { id: 's', label: '90%', zoom: 0.9 },
  { id: 'm', label: '100%', zoom: 1 },
  { id: 'l', label: '110%', zoom: 1.1 },
  { id: 'xl', label: '120%', zoom: 1.2 },
  { id: 'xxl', label: '130%', zoom: 1.3 }
];
